import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Ban, Download, Loader2, RefreshCw, RotateCcw, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { usePageTitle } from '@/components/layout/page-title';
import { ApiError } from '@/features/auth/api';
import { AmountPill } from '@/features/invoices/AmountPill';
import { formatDate, formatDateTime, formatMoney } from '@/features/invoices/format';
import {
  isIssued,
  outboundUpoUrl,
  outboundXmlUrl,
  useAbandonOutbound,
  useOutboundInvoice,
  useResendOutbound,
  useRetryOutbound,
  type IssuanceState,
  type OutboundEvent,
  type OutboundSubmission,
} from './api';
import { StatusBadge } from './StatusBadge';

/** KSeF's duplicate-invoice rejection code — the only one a renumber-and-resend can recover. */
const DUPLICATE_REJECTION_CODE = 440;

/** States from which the user may abandon the invoice (mirrors the server state machine). */
const ABANDONABLE: ReadonlySet<IssuanceState> = new Set([
  'QUEUED',
  'RETRY_WAIT',
  'NEEDS_ATTENTION',
  'REJECTED',
]);

/**
 * The status/timeline page for one outbound (issued) invoice at `/invoices/sent/:id`. Shows the live
 * lifecycle state (polling while in-flight), the KSeF identifiers/dates, the audit timeline, and each
 * submission attempt — plus the recovery actions the operator runbook describes: retry a
 * needs-attention row, renumber-and-resend a 440 duplicate, or abandon. Downloads the accepted FA(3)
 * and the UPO once each exists.
 */
export function OutboundInvoicePage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.resolvedLanguage ?? i18n.language;
  const { id = '' } = useParams();

  const query = useOutboundInvoice(id);
  const detail = query.data;
  const invoice = detail?.invoice;
  usePageTitle(invoice?.invoiceNumber ?? '');

  const retry = useRetryOutbound(id);
  const abandon = useAbandonOutbound(id);
  const resend = useResendOutbound(id);
  const acting = retry.isPending || abandon.isPending || resend.isPending;

  const latestAttempt = detail?.attempts.at(-1) ?? null;
  const hasUpo = detail?.attempts.some((a) => a.hasUpo) ?? false;
  const canResend =
    invoice?.state === 'REJECTED' && latestAttempt?.rejectionCode === DUPLICATE_REJECTION_CODE;
  const canRetry = invoice?.state === 'NEEDS_ATTENTION';
  const canAbandon = invoice != null && ABANDONABLE.has(invoice.state);

  const actionError = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : undefined;
    const key =
      code === 'NOT_A_DUPLICATE'
        ? 'invoices.outbound.actions.errors.not_a_duplicate'
        : code === 'INVALID_STATE' || code === 'STATE_CHANGED'
          ? 'invoices.outbound.actions.errors.state_changed'
          : 'invoices.outbound.actions.errors.failed';
    toast.error(t(key));
  };

  const onRetry = () =>
    retry.mutate(undefined, {
      onSuccess: () => toast.success(t('invoices.outbound.actions.toasts.retried')),
      onError: actionError,
    });
  const onAbandon = () =>
    abandon.mutate(undefined, {
      onSuccess: () => toast.success(t('invoices.outbound.actions.toasts.abandoned')),
      onError: actionError,
    });
  const onResend = () =>
    resend.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(t('invoices.outbound.actions.toasts.resent', { number: r.invoiceNumber })),
      onError: actionError,
    });

  return (
    <div className="flex h-full flex-col">
      {query.isPending ? (
        <Skeleton className="h-72 w-full" />
      ) : query.isError || !invoice || !detail ? (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            {t('invoices.outbound.detail_load_error')}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="flex flex-wrap items-center gap-2 text-2xl font-semibold tracking-tight">
              <span className="max-md:hidden">{invoice.invoiceNumber}</span>
              <StatusBadge state={invoice.state} />
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              {isIssued(invoice.state) ? (
                <Button asChild variant="outline">
                  <a href={outboundXmlUrl(invoice.id)} download={`${invoice.invoiceNumber}.xml`}>
                    <Download data-icon="inline-start" />
                    {t('invoices.details.download_xml')}
                  </a>
                </Button>
              ) : null}
              {hasUpo ? (
                <Button asChild variant="outline">
                  <a
                    href={outboundUpoUrl(invoice.id)}
                    download={`${invoice.invoiceNumber}-upo.xml`}
                  >
                    <ShieldCheck data-icon="inline-start" />
                    {t('invoices.outbound.download_upo')}
                  </a>
                </Button>
              ) : null}
              {canRetry ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={acting}
                  onClick={onRetry}
                >
                  {retry.isPending ? (
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                  ) : (
                    <RotateCcw data-icon="inline-start" />
                  )}
                  {t('invoices.outbound.actions.retry')}
                </Button>
              ) : null}
              {canResend ? (
                <Button
                  type="button"
                  disabled={acting}
                  onClick={onResend}
                >
                  {resend.isPending ? (
                    <Loader2 data-icon="inline-start" className="animate-spin" />
                  ) : (
                    <RefreshCw data-icon="inline-start" />
                  )}
                  {t('invoices.outbound.actions.resend')}
                </Button>
              ) : null}
              {canAbandon ? (
                <ConfirmDialog
                  title={t('invoices.outbound.actions.confirm.abandon_title')}
                  description={t('invoices.outbound.actions.confirm.abandon_description')}
                  confirmLabel={t('invoices.outbound.actions.abandon')}
                  destructive
                  onConfirm={onAbandon}
                >
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={acting}
                  >
                    <Ban data-icon="inline-start" />
                    {t('invoices.outbound.actions.abandon')}
                  </Button>
                </ConfirmDialog>
              ) : null}
            </div>
          </div>

          <ScrollArea className="cn-vertical-scroll -mr-3 mt-6 min-h-0 flex-1">
            <div className="space-y-6 pr-3">
              {invoice.needsAttentionReason ? (
                <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3.5 py-2.5 text-sm text-amber-700 dark:text-amber-300">
                  {invoice.needsAttentionReason}
                </div>
              ) : null}

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Section title={t('invoices.outbound.section.parties')}>
                  <Field label={t('invoices.details.buyer')}>{invoice.buyerName}</Field>
                  {invoice.buyerNip ? <Field label="NIP">{invoice.buyerNip}</Field> : null}
                </Section>

                <Section title={t('invoices.outbound.section.dates')}>
                  <Field label={t('invoices.details.issued')}>
                    {formatDate(lng, invoice.issueDate)}
                  </Field>
                  {invoice.acquisitionDate ? (
                    <Field label={t('invoices.details.acquisition_date')}>
                      {formatDateTime(lng, invoice.acquisitionDate)}
                    </Field>
                  ) : null}
                </Section>

                <Section title={t('invoices.outbound.section.ksef')}>
                  <Field label={t('invoices.outbound.number')}>{invoice.invoiceNumber}</Field>
                  <Field label={t('invoices.outbound.number_source')}>
                    {t(`invoices.outbound.number_source_value.${invoice.numberSource}`)}
                  </Field>
                  {invoice.ksefNumber ? (
                    <Field label={t('invoices.details.ksef_number')}>
                      <span className="font-mono text-xs break-all">{invoice.ksefNumber}</span>
                    </Field>
                  ) : null}
                </Section>
              </div>

              <div className="flex justify-end">
                <AmountPill
                  label={t('invoices.details.gross')}
                  value={formatMoney(lng, Number(invoice.grossTotal), invoice.currency)}
                />
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle>{t('invoices.outbound.section.timeline')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Timeline events={detail.timeline} lng={lng} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle>{t('invoices.outbound.section.attempts')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Attempts attempts={detail.attempts} lng={lng} />
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </>
      )}
    </div>
  );
}

/** The audit timeline: each recorded state transition, oldest first, with actor + time. */
function Timeline({ events, lng }: { events: OutboundEvent[]; lng: string }) {
  const { t } = useTranslation();
  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('invoices.outbound.timeline_empty')}</p>;
  }
  return (
    <ol className="space-y-3">
      {events.map((event, i) => (
        <li key={i} className="flex gap-3">
          <div className="mt-1 flex flex-col items-center">
            <span className="size-2 shrink-0 rounded-full bg-primary/60" />
            {i < events.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
          </div>
          <div className="min-w-0 flex-1 pb-1">
            <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
              <StatusBadge state={event.toState} animate={false} />
              <span className="text-xs font-normal text-muted-foreground">
                {t(`invoices.outbound.actor.${event.actor}`, { defaultValue: event.actor })}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {formatDateTime(lng, event.occurredAt)}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The submission attempts: every send to KSeF, its number, outcome, and any rejection detail. */
function Attempts({ attempts, lng }: { attempts: OutboundSubmission[]; lng: string }) {
  const { t } = useTranslation();
  if (attempts.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('invoices.outbound.attempts_empty')}</p>;
  }
  return (
    <ul className="divide-y">
      {attempts.map((attempt) => (
        <li
          key={attempt.attemptNo}
          className="flex flex-wrap items-start justify-between gap-2 py-2"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium">
              {t('invoices.outbound.attempt_no', { n: attempt.attemptNo })} ·{' '}
              {attempt.invoiceNumber}
            </div>
            {attempt.rejectionCode != null ? (
              <div className="text-xs text-destructive">
                {t('invoices.outbound.rejection', { code: attempt.rejectionCode })}
                {attempt.rejectionDetail ? ` — ${attempt.rejectionDetail}` : ''}
              </div>
            ) : null}
          </div>
          <div className="text-right text-xs text-muted-foreground">
            <div>{attempt.state}</div>
            <div>{formatDateTime(lng, attempt.createdAt)}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{title}</h2>
      <dl className="divide-y rounded-md border">{children}</dl>
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-3 py-2 text-sm">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium">{children}</dd>
    </div>
  );
}
